import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/common/Card';
import { Loading } from '../../components/common/Loading';
import { Avatar } from '../../components/common/Avatar';
import { ROUTES } from '../../utils/constants';
import { useTenantStore } from '../../stores/tenantStore';
import { getAllCohortStats, type CohortWithStats } from '../../api/cohorts';

interface DashboardProps {
  tenant: string;
}

export function Dashboard({ tenant }: DashboardProps) {
  const navigate = useNavigate();
  const { config } = useTenantStore();
  const groupLabel = config?.customization?.dashboardLabel || 'Group';

  const [cohortStats, setCohortStats] = useState<CohortWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, [tenant]);

  const loadStats = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAllCohortStats(tenant);
      setCohortStats(response.stats);
    } catch (err) {
      console.error('Failed to load cohort stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCohortClick = (cohortId: string) => {
    navigate(ROUTES.coach.groups(tenant), { state: { selectedCohortId: cohortId } });
  };

  if (isLoading) {
    return <Loading message="Loading dashboard..." />;
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-12 text-red-500">
          <p className="text-lg font-medium">Error loading dashboard</p>
          <p className="mt-2">{error}</p>
          <button
            onClick={loadStats}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  if (cohortStats.length === 0) {
    return (
      <Card>
        <div className="text-center py-12 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="mt-4 text-lg font-medium">No {groupLabel.toLowerCase()}s yet</p>
          <p className="mt-2">Create your first {groupLabel.toLowerCase()} to get started</p>
          <button
            onClick={() => navigate(ROUTES.coach.groups(tenant))}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Go to {groupLabel}s
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{groupLabel} Overview</h1>
        <button
          onClick={() => navigate(ROUTES.coach.groups(tenant))}
          className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          Manage {groupLabel}s
        </button>
      </div>

      {/* Cohort Tiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cohortStats.map((cohort) => (
          <CohortTile
            key={cohort.cohortId}
            cohort={cohort}
            onClick={() => handleCohortClick(cohort.cohortId)}
          />
        ))}
      </div>
    </div>
  );
}

interface CohortTileProps {
  cohort: CohortWithStats;
  onClick: () => void;
}

function CohortTile({ cohort, onClick }: CohortTileProps) {
  const { stats, memberCount } = cohort;
  const hasActivity = stats.newMessages > 0 || stats.alerts > 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white rounded-xl shadow-sm border-2 transition-all hover:shadow-md hover:border-primary-300 ${
        hasActivity ? 'border-primary-200' : 'border-gray-100'
      }`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <Avatar
            src={cohort.avatarUrl}
            name={cohort.cohortName}
            size="md"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">
              {cohort.cohortName}
            </h3>
            <p className="text-sm text-gray-500">
              {memberCount} member{memberCount !== 1 ? 's' : ''}
            </p>
          </div>
          {hasActivity && (
            <div className="w-3 h-3 rounded-full bg-primary-500 animate-pulse" />
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="p-4 grid grid-cols-3 gap-2">
        {/* New Messages */}
        <StatBadge
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          }
          label="Messages"
          value={stats.newMessages}
          highlight={stats.newMessages > 0}
          color="blue"
        />

        {/* Replies */}
        <StatBadge
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          label="Replies"
          value={stats.replies}
          color="green"
        />

        {/* Alerts */}
        <StatBadge
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
          label="Alerts"
          value={stats.alerts}
          highlight={stats.alerts > 0}
          color="amber"
        />
      </div>
    </button>
  );
}

interface StatBadgeProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  highlight?: boolean;
  color: 'blue' | 'green' | 'amber';
}

function StatBadge({ icon, label, value, highlight = false, color }: StatBadgeProps) {
  const colorClasses = {
    blue: highlight ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-600',
    green: highlight ? 'bg-green-100 text-green-700' : 'bg-gray-50 text-gray-600',
    amber: highlight ? 'bg-amber-100 text-amber-700' : 'bg-gray-50 text-gray-600',
  };

  return (
    <div className={`flex flex-col items-center p-2 rounded-lg ${colorClasses[color]}`}>
      <div className="mb-1">{icon}</div>
      <span className="text-lg font-semibold">{value}</span>
      <span className="text-xs">{label}</span>
    </div>
  );
}
